import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#0f172a',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#38bdf8',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#0284c7',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#0369a1',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  signatureImage: {
    height: 32,
    maxWidth: 150,
    objectFit: 'contain',
    marginVertical: 2
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const AirToWaterHeatPumpCommissioningPDF = ({ formData = {} }) => {
  const installer = formData.installerDetails || {};
  const site = formData.siteDetails || {};
  const equip = formData.equipmentSpecification || {};
  const design = formData.designParameters || {};
  const water = formData.waterQualityAndAntifreeze || {};
  const comm = formData.commissioningMeasurements || {};
  const elect = formData.electricalAndControls || {};
  const handover = formData.handoverAndDeclarations || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Air-to-Water Heat Pump Commissioning</Text>
            <Text style={styles.headerSubtitle}>MCS MIS 3005 Handover Checklist & Manufacturer Commissioning Certificate</Text>
          </View>
          <View style={styles.badge}>
            <Text>MCS COMPLIANT</Text>
          </View>
        </View>

        {/* 1 & 2: Installer & Site */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. MCS Certified Installer</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{installer.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>MCS Certificate No.:</Text>
              <Text style={styles.fieldValue}>{installer.mcsNumber || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Lead Engineer:</Text>
              <Text style={styles.fieldValue}>{installer.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone / Email:</Text>
              <Text style={styles.fieldValue}>{installer.engineerPhone || ''} {installer.engineerEmail ? `• ${installer.engineerEmail}` : ''}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Installation Site & Client</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Client Name:</Text>
              <Text style={styles.fieldValue}>{site.clientName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Site Address:</Text>
              <Text style={styles.fieldValue}>{site.installationAddress || 'N/A'}, {site.postCode || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Contact Phone:</Text>
              <Text style={styles.fieldValue}>{site.contactPhone || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Commissioning Date:</Text>
              <Text style={styles.fieldValue}>{site.commissioningDate || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* 3: Equipment */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Heat Pump, Cylinder & Refrigerant Specification</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Make & Model:</Text>
                <Text style={styles.fieldValue}>{equip.hpManufacturer || 'N/A'} {equip.hpModel || ''}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Serial Number:</Text>
                <Text style={styles.fieldValue}>{equip.hpSerialNumber || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Architecture:</Text>
                <Text style={styles.fieldValue}>{equip.systemType || 'Monobloc'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Refrigerant & Charge:</Text>
                <Text style={styles.fieldValue}>{equip.refrigerantType || 'R32'} ({equip.refrigerantChargeKg || 0} kg)</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cylinder Spec:</Text>
                <Text style={styles.fieldValue}>{equip.cylinderMakeModel || 'N/A'} ({equip.cylinderCapacityLtrs || 0} L)</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Buffer Tank Size:</Text>
                <Text style={styles.fieldValue}>{equip.bufferVesselCapacityLtrs ? `${equip.bufferVesselCapacityLtrs} Litres` : 'None'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4 & 5: MCS Design & Water Treatment */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. MCS MIS 3005 Design Hydraulics</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Calculated Heat Loss:</Text>
              <Text style={styles.fieldValue}>{design.designHeatLossKw ? `${design.designHeatLossKw} kW` : 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Design Outdoor / Indoor:</Text>
              <Text style={styles.fieldValue}>{design.externalDesignTempC}°C / {design.internalDesignTempC}°C</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Flow & Return / ΔT:</Text>
              <Text style={styles.fieldValue}>{design.designFlowTempC}°C / {design.designReturnTempC}°C (ΔT {design.designDeltaTC}°C)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Required Design Flow:</Text>
              <Text style={styles.fieldValue}>{design.designFlowRateLpm ? `${design.designFlowRateLpm} L/min` : 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Emitter Type / SCoP:</Text>
              <Text style={styles.fieldValue}>{design.emitterType || 'Radiators'} (SCoP: {design.heatPumpEfficiencyScop || 'N/A'})</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Water Treatment & Glycol</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Flushed to BS 7593:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{water.systemFlushedBS7593 || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Magnetic System Filter:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{water.magneticFilterInstalled || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Antifreeze Brand / Vol:</Text>
              <Text style={styles.fieldValue}>{water.glycolBrand || 'Glycol'} ({water.glycolConcentrationPct || 25}%)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Freeze Protection Temp:</Text>
              <Text style={styles.fieldValue}>{water.freezeProtectionTempC || -11}°C ({water.refractometerReadingBrix || 14}°Bx)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>System Water pH:</Text>
              <Text style={styles.fieldValue}>{water.primaryWaterPh || '8.2'}</Text>
            </View>
          </View>
        </View>

        {/* 6 & 7: Commissioning Readings & Controls */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>6. Live Operational Test Readings</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Measured Primary Flow:</Text>
              <Text style={styles.fieldValue}>{comm.measuredFlowRateLpm ? `${comm.measuredFlowRateLpm} L/min` : 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Measured Flow / Return:</Text>
              <Text style={styles.fieldValue}>{comm.measuredFlowTempC}°C / {comm.measuredReturnTempC}°C (ΔT {comm.measuredDeltaTC}°C)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Expansion Pre-Charge / Fill:</Text>
              <Text style={styles.fieldValue}>{comm.expansionVesselPreChargeBar || 1.5} Bar / {comm.systemColdPressureBar || 1.5} Bar</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Auto Defrost Verified:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{comm.defrostCycleTested || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Legionella Disinfection:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{comm.dhwDisinfectionConfigured || 'Yes'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>7. Electrical & DNO Notification</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>DNO Process:</Text>
              <Text style={styles.fieldValue}>{elect.dnoNotificationStatus || 'Connect & Notify'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>DNO Ref Number:</Text>
              <Text style={styles.fieldValue}>{elect.dnoReferenceNumber || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Outdoor Rotary Isolator:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{elect.isolatorInstalledNearOutdoorUnit || 'Yes'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>RCD / Protection Type:</Text>
              <Text style={styles.fieldValue}>{elect.rcdTypeFitted || 'Type A 30mA'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Controller Location:</Text>
              <Text style={styles.fieldValue}>{elect.controllerLocation || 'Living Area'}</Text>
            </View>
          </View>
        </View>

        {/* Notes */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>8. Engineer Notes & Handover Pack Confirmation</Text>
          <Text style={{ fontSize: 6.5, color: '#334155' }}>
            {handover.engineerComments || 'System commissioned in strict adherence to MCS MIS 3005 and manufacturer commissioning guidelines.'}
          </Text>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>MCS Commissioning Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{installer.engineerName || 'Lead Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Digitally Certified Commissioning Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.commissioningDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Customer / Client Handover Acceptance</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{site.clientName || 'Client'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Operation & Maintenance Pack Received</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{site.commissioningDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            MCS MIS 3005 • Heat Pump Statutory Commissioning & Handover Certificate
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default AirToWaterHeatPumpCommissioningPDF;
